/**
 * The path a user was heading for when the auth middleware bounced them to the
 * login modal, remembered across the login round-trip.
 *
 * The `auth` middleware stashes `to.fullPath` here before redirecting to `/`;
 * `completeBackendAuth` consumes it so the user lands where they meant to go —
 * chiefly the notification settings reached from an email's Unsubscribe link,
 * which is behind auth.
 *
 * sessionStorage rather than a store: it must survive the full-page context of
 * the login flow, and it is scoped to the tab so a second tab does not inherit
 * a stale destination.
 */

const KEY = 'authRedirect'

/**
 * How long an interrupted navigation stays worth honouring.
 *
 * Without a bound, a guest who clicks a guarded link, dismisses the modal and
 * plays for half an hour is teleported off the game the moment they later log
 * in from the header — a destination with no causal link to that login. Long
 * enough to cover reading the email, finding the password and signing in;
 * short enough that an abandoned intent expires with it.
 */
const MAX_AGE_MS = 15 * 60 * 1000

/**
 * Stored as `<epoch-ms>|<path>`. A value with no `<digits>|` prefix is treated
 * as a path with no timestamp and accepted — real paths always start with `/`,
 * so the two forms cannot be confused, and this keeps any value written before
 * this format existed (or by a test writing the bare path) working.
 */
function encode(path: string): string {
  return `${Date.now()}|${path}`
}

function decode(raw: string): { path: string, age: number | null } {
  const separator = raw.indexOf('|')
  if (separator > 0) {
    const stamp = raw.slice(0, separator)
    if (/^\d+$/.test(stamp)) {
      return { path: raw.slice(separator + 1), age: Date.now() - Number(stamp) }
    }
  }
  return { path: raw, age: null }
}

// A control character (0x00-0x1F or DEL), including a raw newline, has no place
// in a route — its presence is a smell of a tampered value. Checked by code
// point rather than a regex so the source carries no literal control bytes.
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if (code < 0x20 || code === 0x7f) return true
  }
  return false
}

/**
 * Only same-origin absolute paths are allowed back out. The value is written by
 * our own middleware, but sessionStorage is attacker-writable, so the guard sits
 * at the point of use: `//host` and `/\host` are how an open redirect is smuggled
 * past a naive "starts with /" check, and a scheme (`javascript:`, `https:`)
 * must never reach router.push.
 */
export function isSafeInternalPath(path: string): boolean {
  if (typeof path !== 'string' || path.length === 0) return false
  if (path[0] !== '/') return false
  if (path[1] === '/' || path[1] === '\\') return false
  if (hasControlChar(path)) return false
  return true
}

export function stashAuthRedirect(path: string): void {
  try {
    sessionStorage.setItem(KEY, encode(path))
  } catch {
    // Private mode / disabled storage — losing the redirect just sends the user
    // to the default landing page, which is acceptable.
  }
}

/**
 * Read the stashed path and remove it in the same breath, so a stale value can
 * never redirect a later, unrelated login. Returns null when there is nothing
 * safe to honour.
 */
export function consumeAuthRedirect(): string | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(KEY)
  } catch {
    return null
  }
  if (raw === null) return null

  // Removal is guarded separately from the read: if the value cannot be
  // cleared, handing it back would redirect not just this login but every
  // later one on the tab. Losing a single redirect is the cheaper failure.
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    return null
  }

  const { path, age } = decode(raw)
  if (age !== null && age > MAX_AGE_MS) return null

  return isSafeInternalPath(path) ? path : null
}
